import React, { useCallback, useMemo } from 'react';
import { PropertyWithDetails } from './types';
import { propertiesApi } from './services/propertyApi';
import { SearchSection } from '../../../components/common/SearchSection';
import './PropertySearchSection.css';

interface PropertySearchSectionProps {
  // Multiple selection props
  selectedPropertyIds?: number[];
  onPropertiesChange?: (propertyIds: number[]) => void;
  // Single selection props
  selectedProperty?: PropertyWithDetails | null;
  onPropertySelect?: (property: PropertyWithDetails | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  maxResults?: number;
  label?: string;
  maxSelections?: number;
  minSelections?: number;
}

export const PropertySearchSection: React.FC<PropertySearchSectionProps> = ({
  selectedPropertyIds,
  onPropertiesChange,
  selectedProperty,
  onPropertySelect,
  placeholder = 'Search properties by name or address...',
  disabled = false,
  className = '',
  maxResults = 10,
  label = "Search Properties",
  maxSelections,
  minSelections
}) => {
  const isSingleSelection = selectedProperty !== undefined || onPropertySelect !== undefined;
  const selectionMode = isSingleSelection ? 'single' : 'multiple';
  const searchFunction = useCallback(async (searchTerm: string) => {
    const response = await propertiesApi.getAll({ search: searchTerm.trim(), limit: 10 });
    return response.data;
  }, []);

  const formatPropertyAddress = (property: PropertyWithDetails) => {
    if (!property.address) return '';
    
    const { street1, street2, unit, city, state, zip } = property.address;
    let address = street1;
    if (street2) address += ` ${street2}`;
    if (unit) address += ` ${unit}`;
    address += `, ${city}, ${state} ${zip}`;
    return address;
  };

  const renderResultItem = React.useCallback((property: PropertyWithDetails, onAdd: () => void, isDisabled?: boolean) => (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      padding: '12px', 
      borderBottom: '1px solid #f0f0f0',
      backgroundColor: '#ffffff'
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: '600', color: '#333', marginBottom: '4px' }}>
          {property.name}
        </div>
        {property.description && (
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
            {property.description}
          </div>
        )}
        {property.address && (
          <div style={{ fontSize: '11px', color: '#888' }}>
            📍 {formatPropertyAddress(property)}
          </div>
        )}
      </div>
      <button
        type="button"
        className="app-btn app-btn-primary btn-small"
        onClick={onAdd}
        style={{ marginLeft: '8px' }}
        disabled={isDisabled}
      >
        Add
      </button>
    </div>
  ), []);

  const renderSelectedItem = React.useCallback((property: PropertyWithDetails, onRemove: () => void) => {
    const canRemove = !minSelections || (selectedPropertyIds?.length ?? 0) > minSelections;

    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        padding: '8px 12px', 
        backgroundColor: '#f8f9fa', 
        border: '1px solid #e9ecef', 
        borderRadius: '4px', 
        marginBottom: '0.5rem' 
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: '600', color: '#333', marginBottom: '4px' }}>
            {property.name}
          </div>
          {property.description && (
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
              {property.description}
            </div>
          )}
          {property.address && (
            <div style={{ fontSize: '11px', color: '#888' }}>
              📍 {formatPropertyAddress(property)}
            </div>
          )}
        </div>
        <button
          type="button"
          className="app-btn app-btn-danger btn-small"
          onClick={onRemove}
          title="Remove property"
          style={{ marginLeft: '8px' }}
          disabled={!canRemove}
        >
          🗑️
        </button>
      </div>
    );
  }, [minSelections, selectedPropertyIds?.length]);

  return (
    <SearchSection<PropertyWithDetails>
      selectionMode={selectionMode}
      selectedIds={selectedPropertyIds}
      onSelectionChange={onPropertiesChange}
      selectedItem={selectedProperty}
      onItemSelect={onPropertySelect}
      searchFunction={searchFunction}
      getByIdFunction={propertiesApi.getById}
      renderResultItem={renderResultItem}
      renderSelectedItem={renderSelectedItem}
      getItemId={useCallback((property: PropertyWithDetails) => property.id, [])}
      label={label}
      placeholder={placeholder}
      disabled={disabled}
      maxSelections={maxSelections}
      entityType="property"
      emptyStateMessage="No property selected."
      selectedLabel="Selected Property"
    />
  );
};
